/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { GeneralModal, ModalButton } from '../index';
import { Typography } from '@mui/material';
import { CustomTextField, SelectDropdown } from '../index';
import { MenuItem, TextField, FormHelperText } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { isBefore } from 'date-fns';
import { useState } from 'react';

export const AddTimesheetModal = (props) => {
  const empData = props.allEmpData;
  const workDescRegex = /[A-Za-z0-9.,:;()-\s]{0,100}/;
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [hasSelectedEmp, setHasSelectedEmp] = useState(false);
  const [enteredCheckIn, setEnteredCheckIn] = useState(null);
  const [enteredCheckOut, setEnteredCheckOut] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [selectedWorkSetup, setSelectedWorkSetup] = useState('');
  const [hasSelectedSetup, setHasSelectedSetup] = useState(false);
  const [enteredWorkDesc, setEnteredWorkDesc] = useState('');
  const [isAllowed, setIsAllowed] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const newTimesheet = {
    empId: selectedEmpId,
    checkIn: enteredCheckIn,
    checkOut: enteredCheckOut,
    workSetup: selectedWorkSetup,
    workDesc: enteredWorkDesc,
    ref: new Date(),
  };

  const handleSave = () => {
    props.onSave(newTimesheet);
  };

  const isEmployeeCheckIn = (selectedId) => {
    const enteredEmp = empData.filter((emp) => {
      return emp._id === selectedId;
    });
    enteredEmp[0].active === true ? setIsActive(true) : setIsActive(false);
  };

  return (
    <GeneralModal open={props.open} onClose={props.onClose}>
      <Typography sx={{ mb: 2, fontWeight: 600 }} variant="h5">
        Add New Timesheet:
      </Typography>
      <SelectDropdown
        margin="normal"
        required
        fullWidth
        label="Employee"
        error={isActive}
        value={selectedEmpId}
        onChange={(event) => {
          setSelectedEmpId(event.target.value.trim());
          setHasSelectedEmp(true);
          isEmployeeCheckIn(event.target.value.trim());
        }}
      >
        {empData.map((emp) => (
          <MenuItem key={emp._id} value={emp._id}>
            {emp.lastname + ', ' + emp.firstname}
          </MenuItem>
        ))}
      </SelectDropdown>
      {isActive && (
        <FormHelperText variant="filled" error={true}>
          {'Cannot add timesheet to currently checked in employees.'}
        </FormHelperText>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateTimePicker
          renderInput={(props) => (
            <TextField
              {...props}
              margin="normal"
              fullWidth
              required={true}
              error={hasSelectedEmp && !isActive && !isEmpty && !isValid}
              disabled={!hasSelectedEmp || isActive}
              helperText={
                !hasSelectedEmp || isActive
                  ? ''
                  : !isValid && !isEmpty
                    ? 'Invalid check in. It must be before check out time.'
                    : ''
              }
            />
          )}
          disabled={!hasSelectedEmp || isActive}
          label="Check In"
          value={enteredCheckIn}
          onChange={(newValue) => {
            setEnteredCheckIn(newValue);
            if (!newValue && !enteredCheckOut) {
              setIsEmpty(true);
            } else {
              setIsEmpty(false);
            }
            if (isBefore(new Date(newValue), new Date(enteredCheckOut))) {
              setIsValid(true);
            } else {
              setIsValid(false);
            }
          }}
        />
        <DateTimePicker
          renderInput={(props) => (
            <TextField
              {...props}
              margin="normal"
              error={hasSelectedEmp && !isActive && !isEmpty && !isValid}
              disabled={!hasSelectedEmp || isActive}
              helperText={
                !hasSelectedEmp || isActive
                  ? ''
                  : !isValid && !isEmpty
                    ? 'Invalid check out. It must be after check in time.'
                    : ''
              }
              fullWidth
              required={true}
            />
          )}
          disabled={!hasSelectedEmp || isActive}
          label="Check Out"
          value={enteredCheckOut}
          onChange={(newValue) => {
            setEnteredCheckOut(newValue);
            if (!newValue && !enteredCheckOut) {
              setIsEmpty(true);
            } else {
              setIsEmpty(false);
            }
            if (isBefore(new Date(enteredCheckIn), new Date(newValue))) {
              setIsValid(true);
            } else {
              setIsValid(false);
            }
          }}
        />
      </LocalizationProvider>
      <SelectDropdown
        required={true}
        sx={{ minWidth: 200 }}
        label="Work Setup"
        margin="normal"
        fullWidth
        disabled={!hasSelectedEmp || isActive}
        value={selectedWorkSetup}
        onChange={(event) => {
          setSelectedWorkSetup(event.target.value);
          setHasSelectedSetup(true);
        }}
      >
        <MenuItem value={1}>On-site</MenuItem>
        <MenuItem value={2}>WFH</MenuItem>
      </SelectDropdown>
      <CustomTextField
        error={!isAllowed}
        errorMsg={
          'Invalid work description. Only alphanumeric and .,;-() are allowed.'
        }
        label={'Work Description'}
        disabled={!hasSelectedEmp || isActive}
        value={enteredWorkDesc}
        onChange={(event) => {
          const workDesc = event.target.value;
          setEnteredWorkDesc(workDesc);
          workDesc.match(workDescRegex) == workDesc
            ? setIsAllowed(true)
            : setIsAllowed(false);
        }}
      />
      <ModalButton
        primaryBtnName="Save"
        secondaryBtnName="Cancel"
        secondaryClickHandler={props.onClose}
        primaryClickHandler={handleSave}
        disable={!hasSelectedEmp || !isValid || !hasSelectedSetup || !isAllowed}
      />
    </GeneralModal>
  );
};
